import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { DataProviderInstance } from '../../../../api/DataProvider';
import AskPersonButton from '../../../appointments/v2/components/InsuranceAlerts/AskPersonButton';
import GuideUpdatePopup from '../../../appointments/v2/components/InsuranceAlerts/GuideUpdatePopup';
import { hasCriticalAlert } from '../../../appointments/v2/utils/insuranceAlerts';
import useInsurancePending, { PERIODS } from '../../hooks/useInsurancePending';
import { dayLabel, timeLabel } from './pendingFormat';
import { pendingAlertAction } from './pendingActions';
import InsurancePendingResolvePopup from './InsurancePendingResolvePopup';
import PendingRowAlerts from './PendingRowAlerts';
import styles from './InsurancePending.module.css';

/** Quantos atendimentos aparecem antes do "Ver todos" */
const VISIBLE_LIMIT = 5;

const EMPTY_TEXT = {
  today: 'Tudo certo hoje: nenhum documento faltando ou vencido.',
  tomorrow: 'Tudo certo amanhã: nenhum documento faltando ou vencido.',
  week: 'Tudo certo nos próximos 7 dias: nenhum documento faltando ou vencido.',
};

/** Agrupa os atendimentos (já em ordem de horário) por dia */
function groupByDay(items) {
  const days = [];
  items.forEach((item) => {
    const day = item.date.slice(0, 10);
    const last = days[days.length - 1];
    if (last && last.day === day) last.items.push(item);
    else days.push({ day, items: [item] });
  });
  return days;
}

/**
 * "Convênios a conferir" no Início: atendimentos por convênio dos próximos dias
 * com documento faltando ou vencido ("vence em breve" é só aviso: aparece junto,
 * como legenda, mas sozinho não traz o atendimento para cá). Tudo se resolve daqui,
 * sem abrir um atendimento por vez na agenda: cada pendência tem a sua ação (vincular,
 * registrar, marcar a guia autorizada), o ícone do WhatsApp manda a mensagem pronta
 * e "Resolver" (ou o clique na linha) abre os documentos do atendimento.
 * Só aparece com operadora cadastrada.
 */
function InsurancePendingCard() {
  const navigate = useNavigate();
  const { CompanyConfig } = DataProviderInstance();
  const hasProviders = (CompanyConfig?.InsuranceProviders || []).length > 0;
  const { period, setPeriod, status, items, error, reload } = useInsurancePending(hasProviders);
  const [expanded, setExpanded] = useState(false);
  // "Resolver" aberto: id do atendimento e token para abrir já no "Vincular documento"
  const [resolving, setResolving] = useState(null);
  const [guideEdit, setGuideEdit] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);

  if (!hasProviders) return null;

  const shown = expanded ? items : items.slice(0, VISIBLE_LIMIT);
  const criticalCount = items.filter((item) => hasCriticalAlert(item.alerts)).length;

  const changePeriod = (id) => {
    setExpanded(false);
    setPeriod(id);
  };

  const openSchedule = (item) => {
    navigate(`/schedule/appointments?date=${item.date.slice(0, 10)}&appointment=${item.id}`);
  };

  const openResolve = (item, withPicker = false) => {
    setResolving({ id: item.id, snapshot: item, pickerToken: withPicker ? Date.now() : null });
  };

  // Ações dos avisos: na linha abrem o "Resolver" (vínculo) ou a guia por cima do card
  const actionFor = (item) => pendingAlertAction({
    onLink: () => openResolve(item, true),
    onGuide: (alert, status) => setGuideEdit({ item, guideId: alert.document_id, status }),
  });

  // Mudança vinda de fora do bloco de documentos (guia pelo aviso): recarrega o card e
  // os vínculos do "Resolver". O próprio bloco já recarrega os vínculos, então só avisa o card.
  const changed = () => {
    reload();
    setRefreshToken((value) => value + 1);
  };

  // Item do "Resolver" atualizado pelo card; resolvido, ele sai da lista e fica sem avisos
  const resolvingItem = resolving
    ? (items.find((item) => item.id === resolving.id) || { ...resolving.snapshot, alerts: [] })
    : null;

  let body;
  if (status === 'error') {
    body = (
      <div className={styles.empty}>
        <p>{error}</p>
        <button type="button" className={styles.linkButton} onClick={reload}>Tentar de novo</button>
      </div>
    );
  } else if (status === 'loading' && items.length === 0) {
    body = <div className={styles.loading}><CircularProgress size={22} /></div>;
  } else if (items.length === 0) {
    body = <p className={styles.allGood}>{EMPTY_TEXT[period]}</p>;
  } else {
    body = (
      <>
        <div className={styles.days}>
          {groupByDay(shown).map(({ day, items: dayItems }) => (
            <section key={day} className={styles.day}>
              <h3 className={styles.dayTitle}>{dayLabel(day)}</h3>
              <ul className={styles.rows}>
                {dayItems.map((item) => (
                  <li key={item.id} className={styles.row} onClick={() => openResolve(item)}>
                    <span className={styles.rowTime}>{timeLabel(item.date)}</span>
                    <div className={styles.rowMain}>
                      <button type="button" className={styles.rowHead} title="Resolver">
                        <span className={styles.rowName}>{item.patient_name || 'Sem nome'}</span>
                        <span className={styles.rowProvider}>{item.provider_name}</span>
                      </button>
                      <PendingRowAlerts alerts={item.alerts} alertAction={actionFor(item)} />
                    </div>
                    <div className={styles.rowActions}>
                      <AskPersonButton
                        alerts={item.alerts}
                        patientId={item.patient_id}
                        date={item.date}
                        providerName={item.provider_name}
                        compact
                      />
                      <button
                        type="button"
                        className={styles.resolveButton}
                        onClick={(event) => { event.stopPropagation(); openResolve(item); }}
                      >
                        Resolver
                        <ChevronRightIcon sx={{ fontSize: 18 }} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
        {items.length > VISIBLE_LIMIT && (
          <button type="button" className={styles.more} onClick={() => setExpanded(!expanded)}>
            {expanded ? 'Mostrar menos' : `Ver todos (${items.length})`}
          </button>
        )}
      </>
    );
  }

  return (
    <section className={styles.card} aria-label="Convênios a conferir">
      <header className={styles.header}>
        <div className={styles.headerText}>
          <h2 className={styles.title}>
            Convênios a conferir
            {criticalCount > 0 && <span className={styles.badge}>{criticalCount}</span>}
          </h2>
          <p className={styles.subtitle}>Documento faltando ou vencido.</p>
        </div>
        <Tooltip title="Conferir de novo" arrow>
          <span>
            <IconButton size="small" onClick={reload} disabled={status === 'loading'} aria-label="Conferir de novo">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </header>

      <div className={styles.periods} role="group" aria-label="Período">
        {PERIODS.map((p) => (
          <button
            key={p.id}
            type="button"
            className={`${styles.period} ${period === p.id ? styles.periodActive : ''}`}
            onClick={() => changePeriod(p.id)}
            aria-pressed={period === p.id}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className={styles.body}>{body}</div>

      {resolvingItem && (
        <InsurancePendingResolvePopup
          item={resolvingItem}
          alertAction={pendingAlertAction({
            onLink: () => setResolving((prev) => ({ ...prev, pickerToken: Date.now() })),
            onGuide: (alert, status) => setGuideEdit({ item: resolvingItem, guideId: alert.document_id, status }),
          })}
          pickerToken={resolving.pickerToken}
          refreshToken={refreshToken}
          onChanged={reload}
          onOpenSchedule={() => openSchedule(resolvingItem)}
          onClose={() => setResolving(null)}
        />
      )}
      {guideEdit && (
        <GuideUpdatePopup
          patientId={guideEdit.item.patient_id}
          guideId={guideEdit.guideId}
          status={guideEdit.status}
          onSaved={changed}
          onClose={() => setGuideEdit(null)}
        />
      )}
    </section>
  );
}

export default InsurancePendingCard;
